"use client";

import { useCallback, useState } from "react";

/**
 * @param {{
 *   metaTitle?: string;
 *   metaDescription?: string;
 *   imageUrl?: string;
 *   url?: string;
 *   className?: string;
 *   children: import("react").ReactNode;
 * }} props
 */
const ShareButton = ({
  metaTitle,
  metaDescription,
  imageUrl,
  url,
  className = "",
  children,
}) => {
  const [isSharing, setIsSharing] = useState(false);

  const getMetaContent = (selectors) => {
    if (typeof document === "undefined") {
      return "";
    }

    for (const selector of selectors) {
      const content = document.querySelector(selector)?.getAttribute("content");

      if (content) {
        return content;
      }
    }

    return "";
  };

  const handleShare = useCallback(async () => {
    if (typeof navigator === "undefined") {
      return;
    }

    const shareUrl = url || window.location.href;
    const shareTitle =
      metaTitle ||
      getMetaContent(['meta[property="og:title"]', 'meta[name="twitter:title"]']) ||
      document.title;
    const shareText =
      metaDescription ||
      getMetaContent([
        'meta[name="description"]',
        'meta[property="og:description"]',
        'meta[name="twitter:description"]',
      ]) ||
      shareTitle;

    const baseShareData = {
      title: shareTitle,
      text: shareText,
      url: shareUrl,
    };

    try {
      setIsSharing(true);

      if (imageUrl && navigator.canShare) {
        try {
          const response = await fetch(imageUrl);

          if (response.ok) {
            const blob = await response.blob();
            const extension = blob.type.split("/")[1] || "jpg";
            const file = new File([blob], `partage.${extension}`, {
              type: blob.type || "image/jpeg",
            });
            const shareWithFile = { ...baseShareData, files: [file] };

            if (navigator.canShare(shareWithFile)) {
              await navigator.share(shareWithFile);
              return;
            }
          }
        } catch {
          // Fallback: share URL only (preview via Open Graph).
        }
      }

      if (navigator.share) {
        await navigator.share(baseShareData);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Share failed:", error);
      }
    } finally {
      setIsSharing(false);
    }
  }, [metaTitle, metaDescription, imageUrl, url]);

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={isSharing}
      className={className}
    >
      {isSharing ? "Partage…" : children}
    </button>
  );
};

export default ShareButton;
